import type { AssignmentSegment } from "../../domain/round/types";
import type { RoundWorkspacePayload } from "../../lib/api";

function safeFilename(value: string) {
  return value.normalize("NFKD").replace(/[^a-zA-Z0-9_-]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 90) || "export";
}

function clock(minutes: number | null | undefined) {
  if (minutes == null || !Number.isFinite(minutes)) return "—";
  const value = Math.max(0, Math.round(minutes));
  return `${String(Math.floor(value / 60)).padStart(2, "0")}:${String(value % 60).padStart(2, "0")}`;
}

function download(filename: string, blob: Blob) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a"); link.href = url; link.download = filename; document.body.appendChild(link); link.click(); link.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 500);
}

function fit(ctx: CanvasRenderingContext2D, value: string, maxWidth: number) {
  if (ctx.measureText(value).width <= maxWidth) return value;
  let text = value;
  while (text.length > 3 && ctx.measureText(`${text}…`).width > maxWidth) text = text.slice(0, -1);
  return `${text}…`;
}

function _holeAfter(tee: number, offset: number): number {
  return ((tee - 1 + offset) % 18) + 1;
}

function _inferFirstHalfHoles(group: { startMinutes: number; finish9Minutes?: number | null; finish18Minutes?: number | null }): number {
  const start = group.startMinutes;
  const total = (group.finish18Minutes ?? start) - start;
  const toFirst = (group.finish9Minutes ?? start) - start;
  if (total <= 0) return 9;
  return Math.min(17, Math.max(1, Math.round((toFirst / total) * 18)));
}

function _holeRangeLabel(start: number, end: number): string {
  if (end >= start) return `${start}-${end}`;
  if (start === 18) return `18, 1-${end}`;
  return `${start}-18, 1-${end}`;
}

// Not every course splits at tee 1 / tee 10 (Wentworth West for BMW PGA 2026
// splits at tee 1 / tee 9, 8+10 not 9+9) - derive the real header labels from
// each group's own stored times instead of a fixed "1-9"/"10-18".
function tableRanges(groups: { tee: number | null; startMinutes: number; finish9Minutes?: number | null; finish18Minutes?: number | null }[]): readonly [string, string] {
  const byTee = new Map<number, (typeof groups)[number]>();
  for (const group of groups) {
    const tee = Number(group.tee);
    if (tee && !byTee.has(tee)) byTee.set(tee, group);
  }
  if (byTee.size === 0) return ["1-9", "10-18"] as const;
  const labelsPerTee = [...byTee.entries()].map(([tee, group]) => {
    const firstHalf = _inferFirstHalfHoles(group);
    const firstEnd = _holeAfter(tee, firstHalf - 1);
    const secondStart = _holeAfter(tee, firstHalf);
    const secondEnd = _holeAfter(tee, 17);
    return [_holeRangeLabel(tee, firstEnd), _holeRangeLabel(secondStart, secondEnd)] as const;
  });
  const first = labelsPerTee[0];
  const allSame = labelsPerTee.every(([a, b]) => a === first[0] && b === first[1]);
  return allSame ? first : (["1. połowa", "2. połowa"] as const);
}

function section(ctx: CanvasRenderingContext2D, y: number, title: string, subtitle = "") {
  ctx.fillStyle = "#164d6b"; ctx.fillRect(24, y, 1552, 46);
  ctx.fillStyle = "#ffffff"; ctx.font = "700 20px Arial"; ctx.fillText(title, 42, y + 30);
  if (subtitle) { ctx.font = "13px Arial"; ctx.textAlign = "right"; ctx.fillText(subtitle, 1558, y + 29); ctx.textAlign = "left"; }
  return y + 46;
}

export async function exportCombinedOperationalPng(workspace: RoundWorkspacePayload, tournamentName: string) {
  const schedule = workspace.schedule;
  if (!schedule) throw new Error("Brak rozpiski do eksportu.");
  const trips = [...(workspace.transport?.departures || []), ...(workspace.transport?.returns || [])];
  const groupHeight = 46; const tripHeight = 40;
  const height = 150 + 46 + schedule.groups.length * groupHeight + 30 + 46 + Math.max(1, trips.length) * tripHeight + 90;
  const canvas = document.createElement("canvas"); canvas.width = 1600; canvas.height = height;
  const ctx = canvas.getContext("2d"); if (!ctx) throw new Error("Canvas jest niedostępny.");
  ctx.fillStyle = "#f2f6f7"; ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.fillStyle = "#ffffff"; ctx.fillRect(0, 0, 1600, 122);
  ctx.fillStyle = "#15705e"; ctx.fillRect(24, 20, 6, 74);
  ctx.fillStyle = "#10242d"; ctx.font = "700 30px Arial"; ctx.fillText(tournamentName, 48, 56);
  ctx.font = "600 16px Arial"; ctx.fillText(`Pakiet operacyjny · ${workspace.round} · workspace r${workspace.workspaceRevision}`, 48, 84);
  ctx.textAlign = "right"; ctx.font = "700 14px Arial"; ctx.fillText(workspace.confirmation.confirmed ? "CONFIRMED" : workspace.selectedSource.toUpperCase(), 1550, 55); ctx.textAlign = "left";

  const people = new Map(workspace.staff.map((row) => [row.personId, row.displayName]));
  for (const person of schedule.people) people.set(person.personId, person.displayName);
  const assignments = new Map<string, typeof schedule.assignments>();
  for (const row of schedule.assignments) { const list = assignments.get(row.groupId) || []; list.push(row); assignments.set(row.groupId, list); }
  const names = (groupId: string, segments: AssignmentSegment[]) => (assignments.get(groupId) || []).filter((row) => segments.includes(row.segment)).map((row) => people.get(row.personId) || row.personId).join(" / ");

  const [firstRange, secondRange] = tableRanges(schedule.groups);
  let y = section(ctx, 138, "SCORING", `${schedule.groups.length} grup`);
  ctx.fillStyle = "#dfe8eb"; ctx.fillRect(24, y, 1552, 34);
  ctx.fillStyle = "#314953"; ctx.font = "700 12px Arial";
  [[42,"G"],[90,"TEE"],[145,"START"],[235,"FULL 18"],[620,"DOD. FULL"],[930,firstRange],[1190,secondRange],[1465,"TV / COV"]].forEach(([x,label]) => ctx.fillText(String(label), Number(x), y + 22));
  y += 34;
  for (const group of [...schedule.groups].sort((a,b) => a.startMinutes - b.startMinutes || a.number - b.number)) {
    ctx.fillStyle = "#ffffff"; ctx.fillRect(24, y, 1552, groupHeight - 2);
    ctx.fillStyle = group.tv ? "#2f53c4" : "#15705e"; ctx.fillRect(24, y, 5, groupHeight - 2);
    ctx.fillStyle = "#10242d"; ctx.font = "700 14px Arial"; ctx.fillText(String(group.number), 42, y + 28); ctx.fillText(String(group.tee ?? "—"), 90, y + 28); ctx.fillText(clock(group.startMinutes), 145, y + 28);
    ctx.font = "13px Arial";
    ctx.fillText(fit(ctx, names(group.id,["full18"]), 360), 235, y + 28);
    ctx.fillText(fit(ctx, names(group.id,["extraFull18"]), 285), 620, y + 28);
    ctx.fillText(fit(ctx, names(group.id,["holes1to9","extra1to9"]), 235), 930, y + 28);
    ctx.fillText(fit(ctx, names(group.id,["holes10to18","extra10to18"]), 235), 1190, y + 28);
    ctx.font = "700 12px Arial"; ctx.fillText(`${group.tv ? "TV" : "—"} · ${group.coverageScorers ?? group.requiredScorers ?? (group.tv ? 2 : 1)}`, 1465, y + 28);
    y += groupHeight;
  }

  y += 24; y = section(ctx, y, "TRANSPORT", workspace.transport ? `rano max ${workspace.transport.metrics.morningMaxWaitMinutes} min · powrót max ${workspace.transport.metrics.returnMaxWaitMinutes} min` : "brak planu");
  const vehicleNames = new Map(workspace.resources.vehicles.map((row) => [row.id, row.name]));
  const baseNames = new Map(workspace.resources.bases.map((row) => [row.id, row.name]));
  const drawTrips = (rows: NonNullable<RoundWorkspacePayload["transport"]>["departures"], direction: string) => {
    if (!rows.length) { ctx.fillStyle = "#ffffff"; ctx.fillRect(24, y, 1552, tripHeight - 2); ctx.fillStyle = "#687b86"; ctx.font = "13px Arial"; ctx.fillText(`${direction}: brak kursów`, 42, y + 25); y += tripHeight; return; }
    for (const trip of rows) {
      ctx.fillStyle = "#ffffff"; ctx.fillRect(24, y, 1552, tripHeight - 2); ctx.fillStyle = "#10242d"; ctx.font = "700 13px Arial";
      ctx.fillText(direction, 42, y + 25); ctx.fillText(vehicleNames.get(trip.vehicleId) || trip.vehicleId, 150, y + 25); ctx.fillText(`${clock(trip.departAtMinutes)} → ${clock(trip.arriveAtMinutes)}`, 330, y + 25); ctx.fillText(baseNames.get(trip.baseId) || trip.baseId || "—", 485, y + 25);
      ctx.font = "13px Arial"; const driver = people.get(trip.driverPersonId) || trip.driverPersonId; const passengers = trip.passengerPersonIds.map((id) => people.get(id) || id).join(" / ");
      ctx.fillText(fit(ctx, `K: ${driver}`, 280), 680, y + 25); ctx.fillText(fit(ctx, passengers, 500), 980, y + 25); ctx.font = "700 12px Arial"; ctx.fillText(`${trip.shuttle ? "SHUTTLE" : "KURS"} ${trip.tripNumber}`, 1480, y + 25); y += tripHeight;
    }
  };
  drawTrips(workspace.transport?.departures || [], "DOJAZD"); drawTrips(workspace.transport?.returns || [], "POWRÓT");

  y += 20; ctx.fillStyle = "#536b75"; ctx.font = "12px Arial"; ctx.fillText(`Wygenerowano z Golf Scoring V2 · ${new Date().toLocaleString("pl-PL")}`, 30, y + 24);
  const blob = await new Promise<Blob>((resolve, reject) => canvas.toBlob((value) => value ? resolve(value) : reject(new Error("Nie udało się utworzyć PNG.")), "image/png", 0.96));
  download(`${safeFilename(tournamentName)}-${workspace.round}-combined.png`, blob);
}
